/**
 * Create Claim Modal Component
 */
import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Paperclip, Trash2, Upload } from 'lucide-react';
import { claimsAPI, fileUploadAPI } from '../../lib/api';
import { CLAIM_CATEGORIES } from './constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ClaimCreateModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  isVerifier = false, 
  employees = []
}) => {
  const fileInputRef = useRef(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newClaim, setNewClaim] = useState({
    category: 'travel',
    title: '',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().slice(0, 10),
    receipt_number: '',
    attachment_url: null,
    attachment_name: null
  });

  const resetForm = () => {
    setNewClaim({
      category: 'travel',
      title: '',
      description: '',
      amount: '',
      expense_date: new Date().toISOString().slice(0, 10),
      receipt_number: '',
      attachment_url: null,
      attachment_name: null
    });
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setSelectedEmployeeId('');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload PDF, JPG, or PNG files only');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setAttachmentFile(file);
    setAttachmentPreview(file.name);
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    setNewClaim(prev => ({...prev, attachment_url: null, attachment_name: null}));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreate = async () => {
    if (!newClaim.title.trim() || !newClaim.amount) {
      toast.error('Title and amount are required');
      return;
    }
    
    if (isVerifier && !selectedEmployeeId) {
      toast.error('Please select an employee');
      return;
    }
    
    try {
      setSubmitting(true);
      let claimData = {
        ...newClaim,
        amount: parseFloat(newClaim.amount)
      };
      
      let createdClaim;
      
      // If verifier is creating on behalf of employee
      if (isVerifier && selectedEmployeeId) {
        const response = await claimsAPI.createOnBehalf(selectedEmployeeId, claimData);
        createdClaim = response.data || response;
      } else {
        const response = await claimsAPI.create(claimData);
        createdClaim = response.data || response;
      }
      
      // If there's an attachment file, upload it
      if (attachmentFile && createdClaim?.id) {
        try {
          setUploadingAttachment(true);
          const formData = new FormData();
          formData.append('file', attachmentFile);
          await fileUploadAPI.uploadClaimReceipt(createdClaim.id, formData);
          toast.success('Claim created with receipt attached');
        } catch (uploadErr) {
          console.error('File upload failed:', uploadErr);
          toast.warning('Claim created but attachment upload failed. You can upload it later.');
        } finally {
          setUploadingAttachment(false);
        }
      } else {
        toast.success(isVerifier ? 'Claim submitted for verification' : 'Claim created successfully');
      }
      
      resetForm();
      onClose();
      onSuccess();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isVerifier ? 'New Claim (On Behalf of Employee)' : 'New Expense Claim'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Employee Selection for Verifier */}
          {isVerifier && (
            <div>
              <Label>Employee *</Label>
              <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                <SelectTrigger data-testid="claim-employee-select">
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name || `${emp.first_name} ${emp.last_name}`.trim()} ({emp.employee_code || emp.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">This claim will be submitted on behalf of the selected employee</p>
            </div>
          )}
          <div>
            <Label>Category *</Label>
            <Select value={newClaim.category} onValueChange={(v) => setNewClaim({...newClaim, category: v})}>
              <SelectTrigger data-testid="claim-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLAIM_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title *</Label>
            <Input
              value={newClaim.title}
              onChange={(e) => setNewClaim({...newClaim, title: e.target.value})}
              placeholder="Brief description of expense"
              data-testid="claim-title-input"
            />
          </div>
          <div>
            <Label>Amount (₹) *</Label>
            <Input
              type="number"
              value={newClaim.amount}
              onChange={(e) => setNewClaim({...newClaim, amount: e.target.value})}
              placeholder="0.00"
              data-testid="claim-amount-input"
            />
          </div>
          <div>
            <Label>Expense Date</Label>
            <Input
              type="date"
              value={newClaim.expense_date}
              onChange={(e) => setNewClaim({...newClaim, expense_date: e.target.value})}
              data-testid="claim-date-input"
            />
          </div>
          <div>
            <Label>Receipt/Invoice Number</Label>
            <Input
              value={newClaim.receipt_number}
              onChange={(e) => setNewClaim({...newClaim, receipt_number: e.target.value})}
              placeholder="Optional"
              data-testid="claim-receipt-input"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={newClaim.description}
              onChange={(e) => setNewClaim({...newClaim, description: e.target.value})}
              placeholder="Additional details..."
              rows={3}
              data-testid="claim-description-input"
            />
          </div>
          
          {/* File Attachment */}
          <div>
            <Label>Attachment (Receipt/Invoice)</Label>
            {attachmentPreview ? (
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                <Paperclip className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-700 flex-1 truncate">{attachmentPreview}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={removeAttachment}
                  className="h-7 w-7 p-0"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-slate-300 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">Click to upload PDF, JPG, or PNG</p>
                <p className="text-xs text-slate-400 mt-1">Max 5MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleCreate} 
            disabled={submitting || uploadingAttachment}
            data-testid="create-claim-submit"
          >
            {submitting ? 'Creating...' : (uploadingAttachment ? 'Uploading...' : (isVerifier ? 'Submit Claim' : 'Submit'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimCreateModal;
